/**
 * Classic ↔ new design switch (IMA Design System v1.0.0 handoff, source/09-handoff.md).
 *
 * - Build-time opt-in: the new design ("v2") exists only in a build made with
 *   REACT_APP_ENABLE_V2=true (designFlag.ts). Any other build is classic only — no toggle,
 *   `?design=v2` and saved "v2" preferences are ignored, and the V2App chunk is not loaded.
 * - In a v2-enabled build the default is still the classic design ("legacy"); a doctor opts in.
 * - The choice is stored per doctor account, with a device default for the sign-in screens
 *   (src/shared/storage/uiPreferences.ts). localStorage.ui_design is what the device shows now;
 *   it survives logout together with app_language.
 * - `?design=v2` / `?design=legacy` in the URL is an explicit choice (QA links; v2-enabled builds only).
 * - Switching replaces the whole screen tree, so when a form holds unsaved changes
 *   (shared/utils/unsavedChanges) the switch waits for the doctor's confirmation
 *   (DesignSwitchConfirm). The URL is kept, so deep links keep working in either design.
 * - Sets <html data-design="legacy|v2">; the v2 tokens and styles only apply under
 *   [data-design="v2"], so the classic screens are untouched.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { UI_PREFS_APPLIED_EVENT, UiPrefs, recordUiChoice } from "../../shared/storage/uiPreferences";
import { clearUnsaved, hasUnsavedChanges } from "../../shared/utils/unsavedChanges";
import { Design, allowedDesign, isV2Enabled, readInitialDesign } from "./designFlag";

export type { Design };
export { isV2Enabled };

export const DESIGN_STORAGE_KEY = "ui_design";

function initialDesign(): Design {
  try {
    return readInitialDesign(window.location.search, localStorage, sessionStorage);
  } catch {
    return "legacy";
  }
}

interface DesignContextValue {
  design: Design;
  isV2: boolean;
  /** false unless the build enables v2 (REACT_APP_ENABLE_V2=true) — every toggle is hidden. */
  canSwitch: boolean;
  /** Switches, or asks for confirmation first when a form holds unsaved changes. */
  setDesign: (design: Design) => void;
  toggleDesign: () => void;
  /** A switch waiting for the doctor to confirm discarding unsaved changes. */
  pendingDesign: Design | null;
  confirmDesignSwitch: () => void;
  cancelDesignSwitch: () => void;
}

const DesignContext = createContext<DesignContextValue | undefined>(undefined);

export const DesignProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [design, setDesignState] = useState<Design>(initialDesign);
  const canSwitch = isV2Enabled();

  const [pendingDesign, setPendingDesign] = useState<Design | null>(null);

  const applyDesign = useCallback((next: Design) => {
    if (!isV2Enabled()) return;
    setDesignState(next);
    try {
      recordUiChoice("design", next, localStorage, sessionStorage);
    } catch {
      /* storage full or blocked — the choice still applies for this session */
    }
  }, []);

  const setDesign = useCallback(
    (next: Design) => {
      if (!canSwitch || next === design) return;
      if (hasUnsavedChanges()) {
        setPendingDesign(next);
        return;
      }
      applyDesign(next);
    },
    [canSwitch, design, applyDesign]
  );

  const confirmDesignSwitch = useCallback(() => {
    if (!pendingDesign) return;
    const next = pendingDesign;
    setPendingDesign(null);
    clearUnsaved();
    applyDesign(next);
  }, [pendingDesign, applyDesign]);

  const cancelDesignSwitch = useCallback(() => setPendingDesign(null), []);

  // At sign-in the doctor's own preference is applied (never the previous doctor's).
  useEffect(() => {
    const onApplied = (e: Event) => {
      const prefs = (e as CustomEvent<UiPrefs>).detail;
      if (!prefs) return;
      setDesignState(allowedDesign(prefs.design));
    };
    window.addEventListener(UI_PREFS_APPLIED_EVENT, onApplied);
    return () => window.removeEventListener(UI_PREFS_APPLIED_EVENT, onApplied);
  }, []);

  const toggleDesign = useCallback(() => setDesign(design === "v2" ? "legacy" : "v2"), [design, setDesign]);

  useEffect(() => {
    document.documentElement.dataset.design = design;
  }, [design]);

  const value = useMemo(
    () => ({ design, isV2: design === "v2", canSwitch, setDesign, toggleDesign, pendingDesign, confirmDesignSwitch, cancelDesignSwitch }),
    [design, canSwitch, setDesign, toggleDesign, pendingDesign, confirmDesignSwitch, cancelDesignSwitch]
  );

  return <DesignContext.Provider value={value}>{children}</DesignContext.Provider>;
};

export function useDesign(): DesignContextValue {
  const ctx = useContext(DesignContext);
  if (!ctx) throw new Error("useDesign must be used within a DesignProvider");
  return ctx;
}
